import { useState } from 'react'
import { useAdminProjects } from '@/hooks/useAdminProjects'
import { useUpdateFeatureFlags } from '@/hooks/useFeatureFlags'
import { ProjectComparisonsSection } from './ProjectComparisonsSection'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Search, Image, Film, Save, Check, X, Mail, ChevronDown, ChevronUp, GitCompare } from 'lucide-react'
import { toast } from 'sonner'
import type { Project } from '@/types/database'

interface ProjectFlagState {
  [projectId: string]: {
    enable_clip_thumbnails: boolean
    enable_vfx_shot_thumbnails: boolean
    enable_email_notifications: boolean
    hasChanges: boolean
  }
}

export function AdminProjectsManager() {
  const { data: projects, isLoading } = useAdminProjects()
  const updateFlagsMutation = useUpdateFeatureFlags()
  const [searchQuery, setSearchQuery] = useState('')
  const [flagStates, setFlagStates] = useState<ProjectFlagState>({})
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())

  const filteredProjects = projects?.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getProjectFlags = (project: Project) => {
    if (flagStates[project.id]) {
      return flagStates[project.id]
    }

    const flags = project.custom_fields?.feature_flags || {}
    return {
      enable_clip_thumbnails: flags.enable_clip_thumbnails || false,
      enable_vfx_shot_thumbnails: flags.enable_vfx_shot_thumbnails || false,
      enable_email_notifications: flags.enable_email_notifications || false,
      hasChanges: false,
    }
  }

  const handleToggleFlag = (projectId: string, flagName: 'enable_clip_thumbnails' | 'enable_vfx_shot_thumbnails' | 'enable_email_notifications') => {
    setFlagStates((prev) => {
      const currentProject = projects?.find((p) => p.id === projectId)
      if (!currentProject) return prev

      const current = getProjectFlags(currentProject)
      return {
        ...prev,
        [projectId]: {
          ...current,
          [flagName]: !current[flagName],
          hasChanges: true,
        },
      }
    })
  }

  const handleSaveFlags = async (projectId: string) => {
    const flags = flagStates[projectId]
    if (!flags) return

    try {
      await updateFlagsMutation.mutateAsync({
        projectId,
        flags: {
          enable_clip_thumbnails: flags.enable_clip_thumbnails,
          enable_vfx_shot_thumbnails: flags.enable_vfx_shot_thumbnails,
          enable_email_notifications: flags.enable_email_notifications,
        },
      })

      setFlagStates((prev) => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          hasChanges: false,
        },
      }))

      toast.success('Feature flags updated successfully')
    } catch (error) {
      console.error('Failed to update flags:', error)
      toast.error('Failed to update feature flags')
    }
  }

  const handleResetFlags = (projectId: string) => {
    setFlagStates((prev) => {
      const newState = { ...prev }
      delete newState[projectId]
      return newState
    })
  }

  const toggleProjectExpanded = (projectId: string) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading projects...</p>
        </div>
      </div>
    )
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No projects found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="space-y-2">
        <Label htmlFor="search">Search Projects</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="search"
            type="text"
            placeholder="Search by project name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Projects List */}
      <div className="space-y-3">
        {filteredProjects?.map((project) => {
          const flags = getProjectFlags(project)

          return (
            <div
              key={project.id}
              className="border rounded-lg p-4 space-y-4 hover:bg-accent/50 transition-colors"
            >
              {/* Project Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{project.name}</h3>
                  {project.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      {project.nle_type || 'No NLE'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {flags.hasChanges && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResetFlags(project.id)}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSaveFlags(project.id)}
                      disabled={updateFlagsMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>

              {/* Feature Flags */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t">
                {/* Clip Thumbnails */}
                <div
                  className={`flex items-center justify-between p-3 rounded-md border-2 cursor-pointer transition-all ${
                    flags.enable_clip_thumbnails
                      ? 'bg-primary/10 border-primary'
                      : 'bg-muted/30 border-transparent'
                  }`}
                  onClick={() => handleToggleFlag(project.id, 'enable_clip_thumbnails')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center h-10 w-10 rounded-full ${
                        flags.enable_clip_thumbnails ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}
                    >
                      <Image className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Clip Thumbnails</p>
                      <p className="text-xs text-muted-foreground">
                        Allow thumbnail uploads for clips
                      </p>
                    </div>
                  </div>
                  <div>
                    {flags.enable_clip_thumbnails ? (
                      <Check className="h-5 w-5 text-primary" />
                    ) : (
                      <X className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* VFX Shot Thumbnails */}
                <div
                  className={`flex items-center justify-between p-3 rounded-md border-2 cursor-pointer transition-all ${
                    flags.enable_vfx_shot_thumbnails
                      ? 'bg-primary/10 border-primary'
                      : 'bg-muted/30 border-transparent'
                  }`}
                  onClick={() => handleToggleFlag(project.id, 'enable_vfx_shot_thumbnails')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center h-10 w-10 rounded-full ${
                        flags.enable_vfx_shot_thumbnails ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}
                    >
                      <Film className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">VFX Shot Thumbnails</p>
                      <p className="text-xs text-muted-foreground">
                        Allow thumbnail uploads for VFX shots
                      </p>
                    </div>
                  </div>
                  <div>
                    {flags.enable_vfx_shot_thumbnails ? (
                      <Check className="h-5 w-5 text-primary" />
                    ) : (
                      <X className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Email Notifications */}
                <div
                  className={`flex items-center justify-between p-3 rounded-md border-2 cursor-pointer transition-all ${
                    flags.enable_email_notifications
                      ? 'bg-primary/10 border-primary'
                      : 'bg-muted/30 border-transparent'
                  }`}
                  onClick={() => handleToggleFlag(project.id, 'enable_email_notifications')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center h-10 w-10 rounded-full ${
                        flags.enable_email_notifications ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}
                    >
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Email Notifications</p>
                      <p className="text-xs text-muted-foreground">
                        Send email notifications for events
                      </p>
                    </div>
                  </div>
                  <div>
                    {flags.enable_email_notifications ? (
                      <Check className="h-5 w-5 text-primary" />
                    ) : (
                      <X className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>

              {/* Comparisons Section */}
              <div className="border-t mt-4 pt-4">
                <button
                  onClick={() => toggleProjectExpanded(project.id)}
                  className="flex items-center justify-between w-full text-left hover:text-primary transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <GitCompare className="h-4 w-4" />
                    <span className="font-medium text-sm">Comparisons</span>
                  </div>
                  {expandedProjects.has(project.id) ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>

                {expandedProjects.has(project.id) && (
                  <div className="mt-4">
                    <ProjectComparisonsSection projectId={project.id} />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {filteredProjects?.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No projects match your search</p>
        </div>
      )}
    </div>
  )
}
